// The model's hands on the building itself. One recipe per action edits the store, then the
// tool meshes what it made, checks the consequence, and hands the site the elements to mount.
import { checkBuilding, RECIPES, type BuildParams, type BuildResult } from '../build';
import type { PropsElement } from '../props';
import type { BuildStore, SceneEffect, ToolFn, ToolSpec } from '../types';

export const buildSpec: ToolSpec = {
  name: 'build',
  description:
    'Change the building. add_storey copies the top floor of the building one storey higher, with its walls, floor slab, doors, windows and rooms. add_roof puts a roof on top of the building, a gable one over a rectangular outline and a flat one otherwise. add_stair puts one straight stair on a storey. add_wall draws one wall from start to end. add_slab lays one slab over a footprint. add_opening cuts a door or window into a wall that is already there. set_material puts a material name on the elements in ids. remove deletes the elements in ids. undo takes back the last change. export writes the changed building out as an IFC file. Call this only when the visitor asks to change the building, never to answer a question about it.',
  input_schema: {
    type: 'object',
    properties: {
      action: {
        type: 'string',
        enum: [
          'add_storey',
          'add_roof',
          'add_stair',
          'add_wall',
          'add_slab',
          'add_opening',
          'set_material',
          'remove',
          'undo',
          'export',
        ],
      },
      storey: { type: 'string', description: 'name of the storey to build on or copy from' },
      ids: {
        type: 'array',
        items: { type: 'integer' },
        description: 'remove: what to delete; set_material: what to put the material on',
      },
      start: { type: 'array', items: { type: 'number' }, description: 'add_wall: x, y, z metres' },
      end: { type: 'array', items: { type: 'number' }, description: 'add_wall: x, y, z metres' },
      footprint: {
        type: 'array',
        items: { type: 'array', items: { type: 'number' } },
        description: 'add_slab / add_roof: outline as x, z pairs in metres',
      },
      at: { type: 'number', description: 'add_slab: top of the slab in metres above the ground' },
      thickness: { type: 'number', description: 'metres; add_stair reads it as the tread depth' },
      height: { type: 'number', description: 'metres' },
      width: { type: 'number', description: 'add_stair: metres' },
      name: { type: 'string' },
      replace: { type: 'boolean', description: 'add_roof: take the old roof off first' },
      host: { type: 'integer', description: 'add_opening: the wall to cut into' },
      offset: { type: 'number', description: 'add_opening: metres along the wall from its start' },
      sill: { type: 'number', description: 'add_opening: metres above the floor' },
      kind: { type: 'string', description: 'add_roof: flat or gable; add_opening: door or window' },
      material: { type: 'string', description: 'set_material: the material name' },
    },
    required: ['action'],
  },
};

export const build =
  (store?: BuildStore): ToolFn =>
  async (input) => {
    if (!store) return { error: 'no editable model is loaded' };
    const action = String(input.action ?? '');
    try {
      if (action === 'export') {
        const bytes = await store.exportStep();
        return { bytes, size: bytes.length };
      }
      if (action === 'undo') return await report(store, () => undo(store));
      if (action === 'remove') return await report(store, () => remove(store, ids(input)));
      const recipe = RECIPES[action];
      if (!recipe) return { error: `unknown action ${action}` };
      return await report(store, () => recipe(store, params(input)));
    } catch (err) {
      return { error: (err as Error)?.message ?? String(err) };
    }
  };

async function report(store: BuildStore, work: () => Promise<BuildResult>) {
  await store.beginBatch();
  let result: BuildResult;
  try {
    result = await work();
  } finally {
    await store.endBatch();
  }
  // elements() only sees an edit once mesh() has refreshed its cache, so mesh first
  await store.mesh(result.created);
  const wanted = new Set(result.created);
  const created = (await store.elements()).filter((e) => wanted.has(e.expressID));
  const findings = await checkBuilding(store, { createdIds: result.created });
  const effect: SceneEffect = { kind: 'build', created, removed: result.removed };
  return {
    created: created.map(summary),
    removed: result.removed,
    note: result.note,
    findings,
    effect,
  };
}

// Undo is one store step because every recipe runs inside one batch.
async function undo(store: BuildStore): Promise<BuildResult> {
  const before = await store.newIds();
  await store.undo();
  const after = new Set(await store.newIds());
  const removed = before.filter((id) => !after.has(id));
  return { created: [], removed, note: `took back ${removed.length} elements` };
}

async function remove(store: BuildStore, targets: number[]): Promise<BuildResult> {
  if (!targets.length) throw new Error('remove needs ids');
  for (const id of targets) await store.remove(id);
  return { created: [], removed: targets, note: `removed ${targets.length} elements` };
}

const summary = (e: PropsElement) => ({
  expressID: e.expressID,
  type: e.type,
  storey: e.storey,
});

function params(input: Record<string, unknown>): BuildParams {
  const out: BuildParams = {};
  if (typeof input.storey === 'string' && input.storey) out.storey = input.storey;
  if (typeof input.name === 'string' && input.name) out.name = input.name;
  if (typeof input.kind === 'string' && input.kind) out.kind = input.kind;
  if (typeof input.material === 'string' && input.material) out.material = input.material;
  if (input.replace != null) out.replace = input.replace === true || input.replace === 'true';
  for (const key of ['at', 'thickness', 'height', 'width', 'host', 'offset', 'sill'] as const) {
    const v = number(input[key]);
    if (v != null) out[key] = v;
  }
  const targets = ids(input);
  if (targets.length) out.ids = targets;
  const start = point3(input.start);
  const end = point3(input.end);
  if (start) out.start = start;
  if (end) out.end = end;
  if (Array.isArray(input.footprint)) {
    const ring = input.footprint
      .map((p) => (Array.isArray(p) ? [number(p[0]), number(p[1])] : null))
      .filter((p): p is [number, number] => !!p && p[0] != null && p[1] != null);
    if (ring.length >= 3) out.footprint = ring;
  }
  return out;
}

const ids = (input: Record<string, unknown>): number[] =>
  Array.isArray(input.ids) ? input.ids.map(Number).filter(Number.isFinite) : [];

function point3(value: unknown): [number, number, number] | null {
  if (!Array.isArray(value) || value.length < 3) return null;
  const nums = value.slice(0, 3).map(number);
  return nums.every((n): n is number => n != null) ? [nums[0]!, nums[1]!, nums[2]!] : null;
}

function number(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}
